import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from '@/context/Modal.context';
import {
  MODAL_TYPES,
  useQualityControlTool,
} from '@/context/Qualitycontrol.context';
import { useCountries } from '@/hooks/use-countries';
import { useQcMode } from '@/hooks/use-qcMode';
import { ErrorField, ErrorType } from '@/types/QcInfo';
import AutocompleteField from '../Common/Field/AutocompleteField';

interface CountryOriginProps {
  errors?: ErrorType[];
}

type Country = {
  label: string;
  value: string;
  isoCode: string;
};

const DEFAULT_COUNTRY = 'IT';

const CountryOrigin = ({ errors }: CountryOriginProps) => {
  const { t } = useTranslation();

  const { originCountry, setOriginCountry } = useQualityControlTool();

  const { isViewMode } = useQcMode();

  const { setModalInfo } = useModal();

  const { data: countries } = useCountries();

  const [country, setCountry] = useState<Country | null>(null);

  const error =
    errors && errors.find((e) => e.field === ErrorField.ORIGIN_COUNTRY);

  // Show confirmation message
  useEffect(() => {
    if (error) {
      setModalInfo({
        modalType: MODAL_TYPES.WARNING_INFO,
        modalInfo: {
          warningMessage: error.message,
        },
      });
    }
  }, [error]);

  const options: Country[] = useMemo(() => {
    if (!countries || !countries.length) return [];
    return countries.map((c) => ({
      label: c.name || 'N/A',
      value: c.legacyId,
      isoCode: c.isoCode,
    }));
  }, [countries]);

  useEffect(() => {
    if (!options.length) return;

    if (!originCountry.legacyId) {
      const defaultCountry =
        options.find((v) => v.isoCode?.toUpperCase() === DEFAULT_COUNTRY) ||
        null;

      if (!defaultCountry) return;

      setOriginCountry({
        legacyId: defaultCountry.value,
        isConfirmed: false,
      });
      setCountry(defaultCountry);
    } else {
      const selectedCountry =
        options.find((v) => v.value === originCountry.legacyId) || null;
      setCountry(selectedCountry);
    }
  }, [options, originCountry.legacyId]);

  return (
    <Stack my={1.5} px={1}>
      <AutocompleteField
        disabled={isViewMode}
        options={options}
        label={t('QC_V2.MAIN.ORIGIN_COUNTRY')}
        value={country}
        onChange={(_, item) => {
          setOriginCountry({
            ...originCountry,
            legacyId: item?.value || '',
          });
        }}
      />

      <FormControlLabel
        sx={{
          display: 'flex',
        }}
        control={
          <Checkbox
            disabled={isViewMode}
            data-testid="origin_country_checkbox"
            checked={originCountry.isConfirmed}
            onChange={(e) => {
              setOriginCountry({
                ...originCountry,
                isConfirmed: e.target.checked,
              });
            }}
          />
        }
        label={
          <Typography variant="body2">
            {t('QC_V2.MAIN.ORIGIN_COUNTRY_CONFIRM')}
          </Typography>
        }
      />
    </Stack>
  );
};

export default CountryOrigin;
